import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import StudentAvatarView from '../../shared/StudentAvatarView';
import { fetchJson } from '../../apiBase';
import { teacherTypography } from '../../design/typography';
import { summarizeCetele } from './ceteleRules';
import CeteleEvaluationRow from './CeteleEvaluationRow';
import CeteleGeneralPanel from './CeteleGeneralPanel';
import CeteleListCard from './CeteleListCard';
import CeteleTemplateCatalog from '../../shared/CeteleTemplateCatalog';
import CeteleDeleteDialog from '../../shared/CeteleDeleteDialog';
import { CETELE_TEMPLATES as TEMPLATES, CETELE_ICONS, getControlNameSuggestions } from '../../shared/ceteleTemplates';

const surface = { p: 2.5, bgcolor: '#fff', border: '1px solid #e8eaf3', borderRadius: '12px', mb: 2,
  boxShadow: '0 5px 22px rgba(49,47,89,.025)' };
const action = { minHeight: 44, ...teacherTypography.action,
  '&:focus-visible': { outline: '3px solid #352179', outlineOffset: 2 } };
const dialogPaper = {
  m: { xs: 1.5, sm: 4 },
  width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
  maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
  '@supports (height: 100dvh)': {
    maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 64px)' },
  },
};
const selectMenuProps = { PaperProps: { sx: {
  maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100% - 24px)',
  '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 24px)' },
  '& .MuiMenuItem-root': { minHeight: 44, whiteSpace: 'normal', overflowWrap: 'anywhere' },
} } };
const ERROR_MESSAGES = new Map([
  ['A control with this title already exists', 'Bu başlıkta bir değerlendirme zaten var.'],
  ['Control not found', 'Değerlendirme bulunamadı.'],
  ['Cetele not found', 'Çetele bulunamadı.'],
  ['Teacher has no access to this class', 'Bu sınıfta işlem yapma yetkin yok.'],
  ['Control was created concurrently; reload and retry', 'Değerlendirmeler güncellendi. Sayfayı yenileyip tekrar dene.'],
  ['A cetele with this name exists in the class', 'Bu sınıfta aynı adla bir çetele zaten var.'],
  ['Unknown template', 'Bu çetele şablonu desteklenmiyor.'],
]);
const EVALUATION_FILTERS = [
  { id: 'all', label: 'Tümü' },
  { id: 'retry', label: 'Sadece Tekrar' },
  { id: 'pass', label: 'Sadece Öğrendi' },
  { id: 'none', label: 'Kayıt Yok' },
];

async function request(path, method, data) {
  const options = method ? { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) } : {};
  const response = await fetchJson(path, options);
  if (!response.ok) {
    let message = 'İşlem gerçekleştirilemedi.';
    try {
      const payload = await response.json();
      if (typeof payload.detail === 'string') message = ERROR_MESSAGES.get(payload.detail) || payload.detail;
    } catch (_) {}
    throw new Error(message);
  }
  return response.json();
}
const clean = input => String(input || '').trim();

export default function CetelesPage({ schoolId }) {
  const base = '/admin/ceteles/' + encodeURIComponent(schoolId);
  const [classes, setClasses] = useState([]);
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [pendingMarks, setPendingMarks] = useState({});
  const pendingMarksRef = React.useRef(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [tpl, setTpl] = useState('english_words');
  const [name, setName] = useState('Kelime Ezberi');
  const [subject, setSubject] = useState('İngilizce');
  const [classId, setClassId] = useState('');
  const [tab, setTab] = useState('general');
  const [filter, setFilter] = useState('all');
  const [controlId, setControlId] = useState('');
  const [controlPage, setControlPage] = useState(0);
  const [controlOpen, setControlOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [controlName, setControlName] = useState('');
  const [drawerStudent, setDrawerStudent] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [lastChange, setLastChange] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const deleteLock = React.useRef(false);
  const deleteFocus = React.useRef(null);
  const createAction = React.useRef(null);
  const controlAction = React.useRef(null);

  const list = useCallback(() => request(base).then(data => setItems(data.items || [])), [base]);

  useEffect(() => {
    if (!schoolId) return undefined;
    let active = true;
    setLoading(true);
    Promise.all([request(base), request('/admin/classes/' + encodeURIComponent(schoolId))])
      .then(([all, roster]) => {
        if (!active) return;
        setItems(all.items || []);
        const available = (Array.isArray(roster) ? roster : []).map(c => ({
          id: c.id, name: c.name || c.code,
        })).filter(c => c.id != null && c.name);
        setClasses(available);
        if (available.length === 1) setClassId(String(available[0].id));
      })
      .catch(() => { if (active) setError('Çeteleler yüklenemedi.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [schoolId, base]);

  const open = async id => {
    setLoading(true); setError('');
    try {
      const data = await request(base + '/' + id);
      setDetail(data); setLastChange(null); setFilter('all'); setTab('general'); setControlPage(0);
      setControlId(String(data.controls[data.controls.length - 1]?.id || ''));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  const choose = key => {
    const template = TEMPLATES.find(t => t.id === key);
    setTpl(key); setName(template.title); setSubject(template.subject);
  };
  const beginCreate = () => {
    choose('english_words');
    setClassId(classes.length === 1 ? String(classes[0].id) : '');
    setError('');
    setCreateOpen(true);
  };
  const create = async () => {
    if (!classId || !clean(name) || !clean(subject)) return;
    setBusy(true); setError('');
    try {
      const created = await request(base, 'POST', {
        class_id: Number(classId), title: clean(name), subject: clean(subject), template_key: tpl,
      });
      setCreateOpen(false); await list(); await open(created.id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const saveControl = async () => {
    if (!detail || !clean(controlName)) return;
    setBusy(true); setError('');
    try {
      const root = base + '/' + detail.id + '/controls';
      const result = await request(root + (editing ? '/' + editing.id : ''),
        editing ? 'PATCH' : 'POST', { title: clean(controlName) });
      const next = await request(base + '/' + detail.id);
      setDetail(next);
      if (!editing) {
        setControlId(String(result.id));
        setFilter('all');
        setTab('evaluation');
      }
      setControlOpen(false); setEditing(null); await list();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const saveName = async () => {
    if (!detail || !clean(name) || !clean(subject)) return;
    setBusy(true); setError('');
    try {
      await request(base + '/' + detail.id, 'PATCH', { title: clean(name), subject: clean(subject) });
      setDetail(old => ({ ...old, title: clean(name), subject: clean(subject) }));
      setRenameOpen(false); await list();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const beginDelete = target => {
    if (busy || pendingMarksRef.current.size) return;
    setDeleteError(''); setDeleteTarget(target);
  };
  const confirmDelete = async () => {
    if (!deleteTarget || deleteLock.current || pendingMarksRef.current.size) return;
    deleteLock.current = true; setBusy(true); setDeleteError('');
    const target = deleteTarget;
    try {
      await request(base + '/' + target.ceteleId
        + (target.kind === 'control' ? '/controls/' + target.id : ''), 'DELETE');
      setLastChange(null);
      if (target.kind === 'cetele') {
        setItems(previous => previous.filter(item => item.id !== target.ceteleId));
        setDetail(null); setControlId(''); setControlPage(0); setDrawerStudent(null);
      } else {
        // Apply the confirmed deletion without another request that could fail after DELETE succeeded.
        const remaining = detail.controls.filter(c => c.id !== target.id);
        setDetail(previous => ({ ...previous, controls: remaining,
          results: previous.results.filter(r => r.control_id !== target.id) }));
        setControlId(String(remaining.at(-1)?.id || '')); setFilter('all'); setControlPage(0);
        setItems(previous => previous.map(item => item.id !== target.ceteleId ? item : {
          ...item, control_count: remaining.length, last_control: remaining.at(-1)?.title || null,
        }));
      }
      deleteFocus.current = target.kind;
      setDeleteTarget(null);
    } catch (e) { setDeleteError('Silinemedi: ' + e.message); }
    finally { deleteLock.current = false; setBusy(false); }
  };
  const mark = async (studentId, value) => {
    if (!detail || !controlId || busy) return;
    const checklistId = detail.id;
    const selectedControlId = Number(controlId);
    const markId = checklistId + ':' + selectedControlId + ':' + studentId;
    // Synchronous lock protects against double taps before React re-renders.
    if (pendingMarksRef.current.has(markId)) return;
    pendingMarksRef.current.add(markId);
    setPendingMarks(previous => ({ ...previous, [markId]: true }));

    const old = detail.results.find(r => r.student_id === studentId
      && r.control_id === selectedControlId);
    const status = old?.status === value ? null : value;
    setLastChange(null);
    setError('');
    try {
      await request(base + '/' + checklistId + '/controls/' + selectedControlId
        + '/students/' + studentId, 'PUT', { status });
      setDetail(currentDetail => {
        // Do not render a late response into a different checklist.
        if (!currentDetail || currentDetail.id !== checklistId) return currentDetail;
        return {
          ...currentDetail,
          results: [
            ...currentDetail.results.filter(r => !(r.student_id === studentId
              && r.control_id === selectedControlId)),
            ...(status ? [{ student_id: studentId, control_id: selectedControlId, status }] : []),
          ],
        };
      });
      setLastChange({ ceteleId: checklistId, controlId: selectedControlId,
        studentId, before: old?.status || null });
    } catch (e) {
      setError('Değerlendirme kaydedilemedi: ' + e.message);
    } finally {
      pendingMarksRef.current.delete(markId);
      setPendingMarks(previous => {
        const next = { ...previous };
        delete next[markId];
        return next;
      });
    }
  };
  const undoChange = async () => {
    const change = lastChange;
    if (!change || undoBusy || busy) return;
    const markId = change.ceteleId + ':' + change.controlId + ':' + change.studentId;
    if (pendingMarksRef.current.has(markId)) return;
    pendingMarksRef.current.add(markId);
    setPendingMarks(previous => ({ ...previous, [markId]: true }));
    setUndoBusy(true); setError('');
    try {
      await request(base + '/' + change.ceteleId + '/controls/' + change.controlId +
        '/students/' + change.studentId, 'PUT', { status: change.before });
      setDetail(current => {
        if (!current || current.id !== change.ceteleId) return current;
        return {
          ...current, results: [
            ...current.results.filter(r => !(r.student_id === change.studentId && r.control_id === change.controlId)),
            ...(change.before ? [{ student_id: change.studentId,
              control_id: change.controlId, status: change.before }] : []),
          ],
        };
      });
      setLastChange(currentChange => (currentChange
        && currentChange.ceteleId === change.ceteleId
        && currentChange.controlId === change.controlId
        && currentChange.studentId === change.studentId)
        ? null : currentChange);
    } catch (e) { setError('Geri alınamadı: ' + e.message); }
    finally {
      pendingMarksRef.current.delete(markId);
      setPendingMarks(previous => {
        const next = { ...previous };
        delete next[markId];
        return next;
      });
      setUndoBusy(false);
    }
  };
  const summary = useMemo(() => summarizeCetele(detail), [detail]);
  const controls = detail?.controls || [];
  const current = controls.find(c => String(c.id) === controlId);
  const controlSuggestions = getControlNameSuggestions(detail?.template_key, controls);
  const displayed = controls.slice(controlPage * 8, controlPage * 8 + 8);
  const pageCount = Math.max(1, Math.ceil(controls.length / 8));
  const currentResults = detail?.results.filter(r => String(r.control_id) === controlId) || [];
  const currentStatuses = new Map(currentResults.map(r => [String(r.student_id), r.status]));
  const evaluationStudents = (detail?.students || []).filter(student => {
    const status = currentStatuses.get(String(student.id));
    return filter === 'all' || (filter === 'none' ? !status : status === filter);
  });
  const evaluationCounts = {
    all: detail?.students.length || 0,
    retry: currentResults.filter(r => r.status === 'retry').length,
    pass: currentResults.filter(r => r.status === 'pass').length,
    none: (detail?.students || []).filter(student => !currentStatuses.get(String(student.id))).length,
  };

  return <Box sx={{ maxWidth: 1220, mx: 'auto', py: { xs: 0, md: 1 }, minWidth: 0,
    fontFamily: teacherTypography.pageTitle.fontFamily, fontSynthesis: 'none' }}>
    {error && !createOpen && !controlOpen && !renameOpen && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
    {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
    {!detail ? <>
      <Paper sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 3.5 }, mb: 3,
        border: '1px solid #dcdafa', borderRadius: 4,
        background: 'linear-gradient(110deg,#eef5ff 0%,#f7efff 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 2, overflow: 'hidden' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#7050d6', fontWeight: 700, fontSize: 12, letterSpacing: '.045em' }}>
            ÖĞRETMENİN TAKİP ALANI
          </Typography>
          <Typography sx={{ ...teacherTypography.pageTitle, mt: 1, fontSize: { xs: 28, md: 34 } }}>
            ▦ Çetelelerim
          </Typography>
          <Typography sx={{ ...teacherTypography.supportingText, mt: 1, maxWidth: 590, fontSize: 14 }}>
            Öğrencilerin öğrendiklerini ve tekrar ihtiyaçlarını kendi belirlediğin başlıklarda takip et.
          </Typography>
        </Box>
        <Box aria-hidden="true" sx={{ fontSize: { xs: 48, sm: 76 }, flexShrink: 0 }}>🐼</Box>
      </Paper>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
        justifyContent="space-between" gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography sx={teacherTypography.sectionTitle}>Çetelelerim</Typography>
            <Chip size="small" label={items.length}
              sx={{ bgcolor: '#f1ebff', color: '#7151d7', fontWeight: 700 }} />
          </Stack>
          <Typography sx={{ ...teacherTypography.supportingText, mt: 0.5 }}>
            Hazır bir şablonla hızlıca başlayabilir veya kendi çeteleni oluşturabilirsin.
          </Typography>
        </Box>
        <Button ref={createAction} variant="contained" sx={{ ...action, bgcolor: '#7151d7',
          boxShadow: '0 5px 15px #7151d733',
          '&:hover': { bgcolor: '#6040c6' }, alignSelf: { xs: 'stretch', sm: 'center' } }}
          onClick={beginCreate}>+ Yeni Çetele</Button>
      </Stack>
      {!loading && items.length === 0 && <Paper sx={surface}>
        <Typography>Henüz çetele oluşturulmadı. Hazır şablon seçebilir veya sıfırdan başlayabilirsin.</Typography>
      </Paper>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))',
        lg: 'repeat(3,minmax(0,1fr))' }, gap: 1.5 }}>
        {items.map(item => <CeteleListCard key={item.id} item={item} onOpen={open}
          onDelete={item => beginDelete({ kind: 'cetele', ceteleId: item.id, title: item.title })} />)}
      </Box>
      <Paper sx={{ ...surface, mt: 2, bgcolor: '#f0f7ff', borderColor: '#d8e8fb' }}>
        <Typography sx={{ color: '#17213c', fontWeight: 700, fontSize: 14 }}>
          ✨ Bir konu, istediğin kadar değerlendirme.
        </Typography>
        <Typography sx={{ ...teacherTypography.supportingText, mt: 0.5 }}>
          “6’lar”, “5. Hafta” veya “23 Nisan Şiiri” gibi başlıklar oluştur.
          Kaydedilen sonuçlar veliler tarafından Öğrenci360’ta görüntülenebilir.
        </Typography>
      </Paper>
    </> : <>
      <Button sx={{ ...action, color: '#6740cb', px: 0.5, mb: { xs: 0.75, sm: 1.25 } }} onClick={() => { setDetail(null); setLastChange(null); setDrawerStudent(null); list().catch(() => {}); }}>← Çetelelerim</Button>
      <Paper data-testid="cetele-detail-header" sx={{
        ...surface, p: 0, mb: 2, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ display: { xs: 'none', md: 'block' },
                ...teacherTypography.metadata, color: '#6146ad', mb: 0.5 }}>
                ÖĞRETMEN PANELİ · ÇETELELERİM
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box aria-hidden="true" sx={{ display: 'grid', placeItems: 'center',
                  width: { xs: 31, md: 35 }, height: { xs: 31, md: 35 },
                  bgcolor: '#f4efff', borderRadius: '8px', flexShrink: 0,
                  fontSize: { xs: 18, md: 20 } }}>
                  {CETELE_ICONS[detail.template_key] || '📋'}
                </Box>
                <Typography component="h1" sx={{ ...teacherTypography.pageTitle,
                  fontSize: { xs: 22, md: 30 }, overflowWrap: 'anywhere' }}>{detail.title}</Typography>
              </Stack>
              <Typography sx={{ display: { xs: 'none', md: 'block' },
                ...teacherTypography.supportingText, mt: 0.5 }}>
                Değerlendirmeleri ve öğrencilerin gelişimini tek yerden takip et.
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
              <IconButton aria-label="Çeteleyi sil" disabled={busy || Boolean(Object.keys(pendingMarks).length)}
                sx={{ width: 44, height: 44, color: '#b91c1c',
                  '&:hover': { bgcolor: '#fef2f2' },
                  '&:focus-visible': { outline: '3px solid #352179', outlineOffset: 2 } }}
                onClick={() => beginDelete({ kind: 'cetele', ceteleId: detail.id, title: detail.title })}>
                <DeleteOutlineIcon />
              </IconButton>
              <Tooltip title="Çetele adını ve dersini düzenle">
                <IconButton aria-label="Çeteleyi düzenle" size="small"
                  sx={{ bgcolor: '#fff', border: '1px solid #dfd4ff', color: '#6740cb',
                    borderRadius: '8px', width: 44, height: 44,
                    '&:focus-visible': { outline: '3px solid #352179', outlineOffset: 2 },
                    '&:hover': { bgcolor: '#f3edff' } }}
                  onClick={() => {
                    setError(''); setName(detail.title); setSubject(detail.subject); setRenameOpen(true);
                  }}>
                  <EditOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }} gap={1} sx={{ mt: 1 }}>
              <Typography sx={{ ...teacherTypography.metadata, minWidth: 0, overflowWrap: 'anywhere' }}>
                {detail.class_code} · {detail.subject} · {controls.length} değerlendirme
              </Typography>
            <Button ref={controlAction} variant="contained" size="small" startIcon={<AddRoundedIcon />}
              sx={{ ...action, bgcolor: '#7354d9', fontSize: 14,
                borderRadius: '8px', px: 1.5, flexShrink: 0, boxShadow: 'none',
                '&:hover': { bgcolor: '#6245c8' } }}
              onClick={() => {
                setError(''); setEditing(null); setControlName(''); setControlOpen(true);
              }}>Yeni Değerlendirme</Button>
          </Stack>
        </Box>
        <Box sx={{ px: { xs: 1, md: 1.5 }, borderTop: '1px solid #e8eaf3' }}>
          <Tabs value={tab} variant="fullWidth" aria-label="Çetele görünümü"
            onChange={(_, next) => setTab(next)}
            textColor="inherit" TabIndicatorProps={{ style: { height: 3,
              borderRadius: '4px 4px 0 0', backgroundColor: '#7544e9' } }}
            sx={{ minHeight: 44,
              '& .MuiTabs-flexContainer': { gap: 0.75 },
              '& .MuiTab-root': { minWidth: 0, minHeight: 44,
                px: 0.5, py: 0.5, color: '#526583', opacity: 1,
                fontSize: { xs: 13, md: 14 }, ...teacherTypography.action,
                borderRadius: '8px 8px 0 0',
                '&.Mui-selected': { color: '#5c32ca', bgcolor: '#f4efff' },
                '&:focus-visible': { outline: '3px solid #352179', outlineOffset: -3 } } }}>
            <Tab value="general" label="Genel Çetele"
              icon={<FormatListBulletedRoundedIcon sx={{ fontSize: 16 }} />} iconPosition="start"/>
            <Tab value="evaluation" label="Değerlendirme"
              icon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"/>
          </Tabs>
        </Box>
      </Paper>
      {tab === 'general' ? <CeteleGeneralPanel
        detail={detail} summary={summary}
        displayed={displayed} page={controlPage} pageCount={pageCount}
        onPage={setControlPage} onStudentDetail={setDrawerStudent}
        onEvaluation={() => setTab('evaluation')}
      /> : <Paper data-testid="cetele-evaluation" sx={{ ...surface, p: { xs: 1.5, sm: 2.5 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
          alignItems={{ sm: 'center' }} gap={1.25} sx={{ mb: 1.75 }}>
          <Box>
            <Typography sx={{ ...teacherTypography.sectionTitle, fontSize: { xs: 17, sm: 20 } }}>
              Seçili Değerlendirmenin Özeti
            </Typography>
            <Typography sx={teacherTypography.metadata}>
              {currentResults.length} / {detail.students.length} öğrenci değerlendirildi
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1}
            sx={{ width: { xs: '100%', sm: 310 }, flexShrink: 0 }}>
            <TextField select size="small" label="Değerlendirme Seç" SelectProps={{ MenuProps: selectMenuProps }} sx={{ minWidth: 0, flex: '1 1 165px',
              maxWidth: { xs: 'none', sm: 220 }, '& .MuiOutlinedInput-root': { minHeight: 44 },
              '& .MuiSelect-select': { py: 1.5 } }}
              disabled={!controls.length}
              value={controlId} onChange={e => setControlId(e.target.value)}>
              {controls.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.title}</MenuItem>)}
            </TextField>
            <Tooltip title="Değerlendirme başlığını düzenle">
              <span>
                <IconButton aria-label="Değerlendirme başlığını düzenle" disabled={!current}
                  sx={{ flexShrink: 0, color: '#664bb7', border: '1px solid #e8dffc',
                    bgcolor: '#f7f4ff', width: 44, height: 44,
                    '&:focus-visible': { outline: '3px solid #352179', outlineOffset: 2 },
                    '&:hover': { bgcolor: '#e9ddff' } }}
                  onClick={() => {
                    setError(''); setEditing(current); setControlName(current.title);
                    setControlOpen(true);
                  }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label="Değerlendirmeyi sil"
              disabled={!current || busy || Boolean(Object.keys(pendingMarks).length)}
              sx={{ width: 44, height: 44, flexShrink: 0, color: '#b91c1c',
                '&:hover': { bgcolor: '#fef2f2' },
                '&:focus-visible': { outline: '3px solid #352179', outlineOffset: 2 } }}
              onClick={() => beginDelete({ kind: 'control', ceteleId: detail.id, id: current.id, title: current.title })}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Box role="group" aria-label="Seçili değerlendirmenin öğrenci filtreleri" sx={{
          display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 1, mb: 1.5,
        }}>
          {EVALUATION_FILTERS.map(item => <Button key={item.id} type="button"
            aria-label={item.label} aria-pressed={filter === item.id} disabled={!current}
            onClick={() => setFilter(item.id)}
            sx={{ ...action, px: 1, py: 1, gap: 0.75, justifyContent: 'space-between',
              minWidth: 0, borderRadius: '8px', fontSize: 13, lineHeight: 1.4,
              border: '1px solid', borderColor: filter === item.id ? '#7354d9' : '#e8eaf3',
              bgcolor: filter === item.id ? '#f4efff' : '#fff', color: '#45556d',
              boxShadow: filter === item.id ? 'inset 0 0 0 1px #7354d9' : 'none',
              '&:hover': { bgcolor: '#faf8ff', borderColor: '#7354d9' },
            }}>
            <span>{item.label}</span><Box component="span" sx={{ fontWeight: 700 }}>{evaluationCounts[item.id]}</Box>
          </Button>)}
        </Box>
        <Divider sx={{ mb: 0.5, borderColor: '#e8eaf1' }} />
        {!current && <Typography sx={{ py: 2 }}>İlk değerlendirmeyi ekle.</Typography>}
        {current && !evaluationStudents.length && <Typography role="status" sx={{
          ...teacherTypography.supportingText, py: 2,
        }}>Bu filtreyle eşleşen öğrenci yok.</Typography>}
        {current && evaluationStudents.map(student => {
          const status = detail.results.find(r => r.student_id === student.id
            && r.control_id === Number(controlId))?.status;
          const key = detail.id + ':' + controlId + ':' + student.id;
          return <CeteleEvaluationRow key={student.id}
            student={student} status={status}
            saving={Boolean(pendingMarks[key])}
            onMark={value => mark(student.id, value)} />;
        })}
        <Typography sx={{ ...teacherTypography.metadata, mt: 2 }}>
          Sonuçlar otomatik kaydedilir. Seçili sonuca tekrar dokunmak kaydı temizler; gerekirse Geri Al kullanabilirsin.
        </Typography>
      </Paper>}
    </>}

    <CeteleDeleteDialog target={deleteTarget} busy={busy} error={deleteError}
      onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} onExited={() => {
        if (deleteFocus.current) {
          (deleteFocus.current === 'cetele' ? createAction : controlAction).current?.focus();
          deleteFocus.current = null;
        }
      }} />
    <Snackbar open={Boolean(lastChange) && !deleteTarget} autoHideDuration={8000}
      onClose={(_, reason) => { if (reason !== 'clickaway') setLastChange(null); }}
      message="Değerlendirme kaydedildi"
      action={<Button color="inherit" size="small" disabled={undoBusy} onClick={undoChange}>Geri Al</Button>}
    />
    <Dialog open={createOpen} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaper }} onClose={() => !busy && setCreateOpen(false)}>
      <DialogTitle component="div" sx={{ pt: 3, px: { xs: 2.5, md: 3.5 },
        '@media (max-height: 600px)': { pt: 1.5, pb: 1,
          '& .cetele-create-eyebrow, & .cetele-create-description': { display: 'none' },
          '& .cetele-create-heading': { fontSize: 22, mt: 0 } } }}>
        <Typography className="cetele-create-eyebrow" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', color: '#7151d7' }}>
          YENİ ÇETELE OLUŞTUR
        </Typography>
        <Typography component="h2" className="cetele-create-heading" sx={{ ...teacherTypography.pageTitle, mt: 0.5 }}>
          Nasıl başlamak istersiniz?
        </Typography>
        <Typography className="cetele-create-description" sx={{ ...teacherTypography.supportingText, mt: 0.5 }}>
          Hazır bir seçenek seçebilir veya tamamen kendinize ait bir çetele oluşturabilirsiniz.
        </Typography>
      </DialogTitle>
      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1, flexShrink: 0 }}>{error}</Alert>}
      <DialogContent sx={{ px: { xs: 2.5, md: 3.5 }, pb: 2 }}>
        <CeteleTemplateCatalog value={tpl} onChange={choose} disabled={busy} />
        <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #e4eaf3',
          bgcolor: '#f8faff' }}>
          <Typography sx={{ ...teacherTypography.sectionTitle, mb: 2 }}>Çetele bilgileri</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1.2fr 1.2fr' }, gap: 1.5 }}>
            <TextField label="Çetele Adı" required inputProps={{ maxLength: 160 }}
              value={name} onChange={e => setName(e.target.value)} />
            <TextField select label="Sınıf" required value={classId} SelectProps={{ MenuProps: selectMenuProps }}
              onChange={e => setClassId(e.target.value)}>
              {classes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label="Ders" required inputProps={{ maxLength: 100 }}
              value={subject} onChange={e => setSubject(e.target.value)} />
          </Box>
          <Typography sx={{ ...teacherTypography.supportingText, fontSize: 12, mt: 1 }}>
            💡 Hazır şablon yalnızca başlık ve ders önerir; öğrenci sonucu oluşturmaz.
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, md: 3.5 }, pb: 3, gap: 1,
        '@media (max-height: 600px)': { py: 1 } }}>
        <Button sx={action} disabled={busy} onClick={() => setCreateOpen(false)}>Vazgeç</Button>
        <Button variant="contained" sx={{ ...action, bgcolor: '#7151d7' }}
          disabled={busy || !classId || !clean(name) || !clean(subject)}
          onClick={create}>Çeteleyi Oluştur →</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={controlOpen} onClose={() => !busy && setControlOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: dialogPaper }}>
      <DialogTitle>{editing ? 'Değerlendirmeyi Düzenle' : 'Yeni Değerlendirme'}</DialogTitle>
      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1, flexShrink: 0 }}>{error}</Alert>}
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
        <TextField autoFocus fullWidth label="Değerlendirme Başlığı" inputProps={{ maxLength: 120 }} value={controlName} onChange={e => setControlName(e.target.value)} />
        {!editing && controlSuggestions.length > 0 && <Box>
          <Typography sx={{ ...teacherTypography.metadata, mb: 1 }}>
            Öneriyi seçebilir veya kendi başlığını yazabilirsin.
          </Typography>
          <Box role="group" aria-label="Değerlendirme adı önerileri" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {controlSuggestions.map(suggestion => <Button key={suggestion} type="button"
              variant="outlined" disabled={busy} aria-label={'Başlık önerisi: ' + suggestion}
              onClick={() => setControlName(suggestion)}
              sx={{ ...action, minWidth: 44, maxWidth: '100%', borderRadius: '8px',
                px: 1.25, py: 1, color: '#6146ad', borderColor: '#9570d9',
                bgcolor: '#faf8ff', fontSize: 13, lineHeight: 1.5, textAlign: 'left',
                overflowWrap: 'anywhere', '&:hover': { bgcolor: '#f4efff', borderColor: '#7354d9' } }}>
              {suggestion}
            </Button>)}
          </Box>
        </Box>}
      </DialogContent>
      <DialogActions>
        <Button sx={action} disabled={busy} onClick={() => setControlOpen(false)}>Vazgeç</Button>
        <Button sx={action} variant="contained" disabled={busy || !clean(controlName)} onClick={saveControl}>Kaydet</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={renameOpen} onClose={() => !busy && setRenameOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: dialogPaper }}>
      <DialogTitle>Çeteleyi Düzenle</DialogTitle>
      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1, flexShrink: 0 }}>{error}</Alert>}
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
        <TextField autoFocus fullWidth label="Çetele Adı" inputProps={{ maxLength: 160 }} value={name} onChange={e => setName(e.target.value)} />
        <TextField fullWidth label="Ders" inputProps={{ maxLength: 100 }} value={subject} onChange={e => setSubject(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button sx={action} onClick={() => setRenameOpen(false)}>Vazgeç</Button>
        <Button sx={action} variant="contained" disabled={busy || !clean(name) || !clean(subject)} onClick={saveName}>Kaydet</Button>
      </DialogActions>
    </Dialog>
    <Drawer open={Boolean(drawerStudent)} anchor="right" onClose={() => setDrawerStudent(null)}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, overflow: 'hidden',
        boxSizing: 'border-box', maxWidth: '100vw', maxHeight: '100vh',
        '@supports (height: 100dvh)': { maxHeight: '100dvh' } } }}>
      {detail && drawerStudent && <>
        <Box data-cetele-student-header sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 2 },
          pb: 2, bgcolor: '#fff', flexShrink: 0, borderBottom: '1px solid #edf0f5' }}>
          <Button sx={action} onClick={() => setDrawerStudent(null)}>← Kapat</Button>
          <Stack direction="row" gap={2} alignItems="center" sx={{ mt: 1 }}>
            <StudentAvatarView studentId={drawerStudent.id} avatarId={drawerStudent.avatar_id} starterEmoji={drawerStudent.starter_emoji} name={drawerStudent.name} size={55} />
            <Typography sx={{ ...teacherTypography.sectionTitle, minWidth: 0, overflowWrap: 'anywhere' }}>{drawerStudent.name}</Typography>
          </Stack>
        </Box>
        <Box data-cetele-student-history role="region" aria-label={drawerStudent.name + ' değerlendirme geçmişi'}
          tabIndex={0} sx={{ px: { xs: 2, sm: 3 }, pb: 2, minHeight: 0, overflowY: 'auto', flex: 1,
            '&:focus-visible': { outline: '3px solid #352179', outlineOffset: -3 } }}>
          {controls.map(c => {
          const value = detail.results.find(r => r.control_id === c.id && r.student_id === drawerStudent.id)?.status;
          return <Stack key={c.id} direction="row" justifyContent="space-between" gap={1.5} sx={{ py: 1.5, borderBottom: '1px solid #edf0f5' }}>
            <Typography sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>{c.title}</Typography><Typography sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} color={value === 'pass' ? 'success.main' : 'text.secondary'}>
              {value === 'pass' ? '✓ Öğrendi' : value === 'retry' ? '↻ Tekrar' : '—'}
            </Typography>
          </Stack>;
          })}
        </Box>
      </>}
    </Drawer>
  </Box>;
}
